import React, { useCallback, useEffect, useState } from "react";
import Card from "@druva-apps/orion/lib/card";
import Col from "@druva-apps/orion/lib/col";
import Heading from "@druva-apps/orion/lib/heading";
import Row from "@druva-apps/orion/lib/row";
import ToggleButton from "@druva-apps/orion/lib/toggleButton";
import { FieldArray } from "formik";
import { endOfDay, addDays } from "date-fns";

import Field from "components/Field";
import {
  dataSourceIdsRequiringEnterpriseOrEliteSku,
  genericSelectTypes,
  productIDToName,
  PRODUCT_ID,
  storageProviderNameToId,
} from "consts";
import { asLocalTime } from "utils/timezone";

const FormContainer = ({
  formikProps,
  isEdit,
  parentMspOptions = [],
  isLoadingMspList,
  isParentMsp = false,
}) => {
  const getEodMoment = useCallback((nativeDate) => {
    return endOfDay(nativeDate);
  }, []);

  const [licenseExpiryMaxDate, setLicenseExpiryMaxDate] = useState(
    isEdit && formikProps.initialValues?.edition?.value === "evaluation"
      ? addDays(endOfDay(new Date()), 30)
      : null,
  );

  const isEvaluationLicense =
    formikProps.values.edition?.value === "evaluation";

  const onLicenseTypeChangeHandler = useCallback(
    ({ value }) => {
      if (value === "evaluation") {
        const maxDate = addDays(endOfDay(new Date()), 30);
        if (!formikProps.values.expiryDate) {
          formikProps.setFieldValue("expiryDate", maxDate);
        }
        setLicenseExpiryMaxDate(maxDate);
        // Clear parent MSP when license type is evaluation
        if (formikProps.values.parentMsp) {
          formikProps.setFieldValue("parentMsp", null);
        }
      } else {
        setLicenseExpiryMaxDate(undefined);
      }
    },
    [formikProps],
  );
  useEffect(() => {
    formikProps.setFieldTouched("expiryDate", true);
  }, []);
  return (
    <>
      <Card
        className="mt-2"
        title="General"
        body={() => {
          return (
            <>
              <Field
                label="Organization Name"
                name="organizationName"
                dataTestId="sportal-add-msp-org-name"
                formikProps={formikProps}
                placeholder="Enter Organization Name"
              />
              <Field
                label="SFDC ID"
                name="sfdcId"
                dataTestId="sportal-add-msp-sfdc-id"
                formikProps={formikProps}
                placeholder="Enter SFDC ID"
                tooltip="SFDC ID should start with '001' and be 15 or 18 characters long"
              />
              <Field
                label="Work Email"
                name="email"
                dataTestId="sportal-add-msp-work-email"
                tooltip="Enter a work email address to add an MSP. Personal email addresses are not supported."
                tooltipProps={{ dataTestId: "sportal-add-msp-email-info" }}
                formikProps={formikProps}
                placeholder="Enter Email"
                disabled
              />
              <Field
                type={genericSelectTypes.editions}
                name="edition"
                label="License Type"
                placeholder="Select License Type"
                dataTestId="sportal-add-msp-edition-drpdwn"
                formikProps={formikProps}
                onChange={onLicenseTypeChangeHandler}
                downgradeAllowed={false}
              />
              <Field
                type="date"
                name="expiryDate"
                label="Expiry Date"
                placeholder="Select Date"
                dataTestId="sportal-add-msp-expiry-date"
                formikProps={formikProps}
                interceptor={getEodMoment}
                maxDate={licenseExpiryMaxDate}
                disabled={!!formikProps.values.parentMsp}
                inputTooltipProps={
                  formikProps.values.parentMsp
                    ? {
                        text: "Cannot be edited as subordinate MSP account will inherit parent MSP account's expiry date.",
                        placement: "bottom",
                        maxWidth: 300,
                      }
                    : undefined
                }
              />
              <Field
                fullWidth
                type="checkbox"
                name="druInvestigate"
                label="Enable Dru Investigate"
                dataTestId="sportal-update-msp-dru-investigate"
                formikProps={formikProps}
              />
              {isEdit && (
                <>
                  <Field
                    type="select"
                    name="parentMsp"
                    label="Parent MSP Account"
                    placeholder="Select"
                    dataTestId="sportal-update-msp-parent-drpdwn"
                    formikProps={formikProps}
                    options={parentMspOptions}
                    isClearable
                    disabled={
                      isEvaluationLicense || isParentMsp || isLoadingMspList
                    }
                    isLoading={
                      !isParentMsp && isLoadingMspList && !isEvaluationLicense
                    }
                    inputTooltipProps={
                      isEvaluationLicense || isParentMsp
                        ? {
                            text: isEvaluationLicense
                              ? "Cannot be added for Evaluation license type."
                              : "Cannot assign a parent account. This MSP is already a parent account and has subordinate accounts",
                            placement: "bottom",
                            maxWidth: 300,
                          }
                        : undefined
                    }
                    onChange={(selectedOption) => {
                      formikProps.setFieldValue("parentMsp", selectedOption);
                      if (selectedOption) {
                        // Set expiry date to parent's expiry date
                        formikProps.setFieldValue(
                          "expiryDate",
                          asLocalTime(selectedOption.expiryDate),
                        );
                      } else {
                        // Restore original MSP's expiry date when parent is cleared
                        formikProps.setFieldValue(
                          "expiryDate",
                          formikProps.initialValues.expiryDate,
                        );
                      }
                    }}
                  />
                  {formikProps.values.parentMsp && (
                    <Row>
                      <Col sm={3}></Col>
                      <Col>
                        <span className="oui-text-muted oui-ml-1">
                          <span className="oui-font-weight-bold">
                            SFDC ID:&nbsp;
                          </span>
                          {formikProps.values.parentMsp.sfdcId}
                        </span>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </>
          );
        }}
      />
      <FieldArray
        name="products"
        render={(_) => (
          <>
            {formikProps.values.products.length > 0 &&
              formikProps.values.products.map((detail, index) => (
                <Card
                  className="mt-4"
                  key={`products-${detail.productID}`}
                  body={() => {
                    const isProductEnabled = detail.enabled;

                    // Disabled Business if only product selected is Endpoints
                    const dataSources = detail.datasources;
                    const disabledEditions =
                      dataSources?.length > 0 &&
                      dataSources.every((ds) =>
                        dataSourceIdsRequiringEnterpriseOrEliteSku.includes(
                          ds.value,
                        ),
                      )
                        ? ["business"]
                        : [];

                    const isProductSassAppsAndEndpoints =
                      detail.productID === PRODUCT_ID.saasAppsAndEndpoints;
                    const isProductEnterpriseWorkloads =
                      detail.productID === PRODUCT_ID.enterpriseWorkloads;

                    return (
                      <>
                        <Row className="oui-mb-4 oui-mt-1">
                          <Col>
                            <Heading
                              text={productIDToName[detail.productID]}
                              inline
                            />
                            <ToggleButton
                              isActive={isProductEnabled}
                              name={`products[${index}].enabled`}
                              onClick={(_, isActive) => {
                                formikProps.setFieldValue(
                                  `products[${index}].enabled`,
                                  isActive,
                                );
                              }}
                              disabled={
                                isEdit &&
                                formikProps.initialValues.products?.[index]
                                  .enabled
                              }
                              validationProps={formikProps}
                              className="oui-d-inline oui-ml-3 oui-align-middle"
                              dataTestId={`sportal-add-msp-toggle-${index}`}
                            />
                            <div className="font-12 oui-text-muted oui-mt-1">
                              {`${productIDToName[detail.productID]} is ${
                                isProductEnabled ? "enabled" : "disabled"
                              }`}
                            </div>
                          </Col>
                        </Row>
                        {isProductEnabled && (
                          <>
                            {isProductSassAppsAndEndpoints && (
                              <Field
                                fullWidth
                                type="checkbox"
                                name={`products[${index}].educationLicense`}
                                label="Enable Education License"
                                formikProps={formikProps}
                              />
                            )}
                            <Field
                              type={genericSelectTypes.skus}
                              isMulti
                              name={`products[${index}].skus`}
                              label="Editions"
                              placeholder="Select"
                              dataTestId="sportal-add-msp-sku-drpdwn-enterprise-workload"
                              formikProps={formikProps}
                              disabledEditions={disabledEditions}
                              onChange={(value) => {
                                formikProps.setFieldValue(
                                  `products[${index}].skus`,
                                  value,
                                );
                                if (
                                  value?.length > 0 &&
                                  !value?.some(
                                    (sku) =>
                                      sku.value === "elite" ||
                                      sku.value === "enterprise",
                                  )
                                ) {
                                  const datasource = (
                                    formikProps.values?.products?.[index]
                                      ?.datasources || []
                                  ).filter(
                                    (ds) =>
                                      !dataSourceIdsRequiringEnterpriseOrEliteSku.includes(
                                        ds.value,
                                      ),
                                  );
                                  formikProps.setFieldValue(
                                    `products[${index}].datasources`,
                                    datasource,
                                  );
                                }
                              }}
                            />
                            <Field
                              type="storage-regions-select"
                              isMulti
                              name={`products[${index}].awsStorageRegions`}
                              label="AWS Storage Regions"
                              placeholder="Select"
                              dataTestId="sportal-update-msp-msp-storage-region-drpdwn-enterprise-workload"
                              formikProps={formikProps}
                              optionsQueryConfig={{
                                productID: detail.productID,
                                storageProvider: storageProviderNameToId.aws,
                              }}
                            />
                            <Field
                              isMulti
                              type="storage-regions-select"
                              name={`products[${index}].azureStorageRegions`}
                              label="Azure Storage Regions"
                              placeholder="Select"
                              dataTestId={
                                isProductEnterpriseWorkloads
                                  ? "sportal-update-msp-azure-storage-region-drpdwn-enterprise-workload"
                                  : `sportal-update-msp-azure-storage-region-drpdwn-saas-${index}`
                              }
                              formikProps={formikProps}
                              optionsQueryConfig={{
                                productID: detail.productID,
                                storageProvider: storageProviderNameToId.azure,
                              }}
                            />
                            {isProductSassAppsAndEndpoints ? (
                              <Field
                                type={genericSelectTypes.datasources}
                                isMulti
                                name={`products[${index}].datasources`}
                                label="Products"
                                placeholder="Select"
                                dataTestId={`sportal-edit-msp-product-drpdwn-${detail.productID}`}
                                formikProps={formikProps}
                                productId={detail.productID}
                                tooltip="Business Edition is not supported for Endpoints, Okta, or Dynamics 365 workloads"
                                isEndpointAllowedForEdition={(
                                  detail.skus || []
                                ).some(
                                  (sku) =>
                                    sku.value === "elite" ||
                                    sku.value === "enterprise",
                                )}
                                onChange={(value) => {
                                  formikProps.setFieldValue(
                                    `products[${index}].datasources`,
                                    value,
                                  );
                                  if (
                                    value?.every((v) =>
                                      dataSourceIdsRequiringEnterpriseOrEliteSku.includes(
                                        v.value,
                                      ),
                                    )
                                  ) {
                                    const skus = (detail.skus || []).filter(
                                      (sku) => sku.value !== "business",
                                    );
                                    formikProps.setFieldValue(
                                      `products[${index}].skus`,
                                      skus,
                                    );
                                  }
                                }}
                              />
                            ) : null}
                          </>
                        )}
                      </>
                    );
                  }}
                />
              ))}
          </>
        )}
      />
    </>
  );
};

export default FormContainer;
